import { useEffect, useState } from "react";

import { MessageSeverity } from "primereact/api";

import { DisclaimerItemDto } from "../../../../../@api";

import { IDisclaimersTabStateOptions } from "../../../../models/interfaces/disclaimers-tab.interface";
import { DisclaimerLanguage, DisclaimerTexts, TDisclaimer } from "../../../../models/types/disclaimer.type";
import { useStore } from "../../../../stores/StoreContext";

export const DISCLAIMER_LANGUAGES: DisclaimerLanguage[] = ["en", "de", "fr"];

const getDisclaimerItemPerLanguage = (
  type: TDisclaimer,
  language: DisclaimerLanguage,
  text: string,
): DisclaimerItemDto => {
  return {
    type,
    language,
    text,
  };
};

const getAvailableDisclaimerLanguages = (type: TDisclaimer): DisclaimerTexts => {
  const items: DisclaimerTexts = {} as DisclaimerTexts;
  DISCLAIMER_LANGUAGES.forEach(language => {
    items[language] = getDisclaimerItemPerLanguage(type, language, "");
  });
  return items;
};

const hydrateTexts = (type: TDisclaimer, list: Array<DisclaimerItemDto>): DisclaimerTexts => {
  const items: DisclaimerTexts = { ...getAvailableDisclaimerLanguages(type) };
  list.forEach((disclaimerItem: DisclaimerItemDto) => {
    if (isDisclaimerLanguage(disclaimerItem.language)) {
      items[disclaimerItem.language] = disclaimerItem;
    }
  });
  return items;
};

const textsAreEqual = (left: DisclaimerTexts, right: DisclaimerTexts): boolean => {
  return DISCLAIMER_LANGUAGES.every(
    language => left[language].text === right[language].text && left[language].type === right[language].type,
  );
};

const isDisclaimerLanguage = (language: string): language is DisclaimerLanguage => {
  return DISCLAIMER_LANGUAGES.includes(language as DisclaimerLanguage);
};

export interface IDisclaimersTabState {
  bingTexts: DisclaimerTexts;
  imageGenTexts: DisclaimerTexts;
  loading: boolean;
  saving: boolean;
  hasChanges: boolean;
  onChangeBing: (language: DisclaimerLanguage, value: string) => void;
  onChangeImageGen: (language: DisclaimerLanguage, value: string) => void;
  onSave: () => void;
  onReset: () => void;
}

export function useDisclaimersTabState(options: IDisclaimersTabStateOptions): IDisclaimersTabState {
  const { toastStore, globalErrorsStore } = useStore();

  // Bing state
  const [bingTexts, setBingTexts] = useState<DisclaimerTexts>(getAvailableDisclaimerLanguages("bing"));
  const [bingOriginal, setBingOriginal] = useState<DisclaimerTexts>(getAvailableDisclaimerLanguages("bing"));
  const [bingInitial, setBingInitial] = useState<DisclaimerTexts>(getAvailableDisclaimerLanguages("bing"));

  // ImageGen state
  const [imageGenTexts, setImageGenTexts] = useState<DisclaimerTexts>(getAvailableDisclaimerLanguages("imagegen"));
  const [imageGenOriginal, setImageGenOriginal] = useState<DisclaimerTexts>(
    getAvailableDisclaimerLanguages("imagegen"),
  );

  // Loading and saving states
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  // Load both disclaimers on mount
  useEffect(() => {
    const loadBoth = async () => {
      setLoading(true);
      try {
        // Load Bing disclaimers
        const bingList = await options.bingOptions.loadDisclaimerTexts();
        const bingItems = hydrateTexts("bing", bingList);
        setBingTexts(bingItems);
        setBingOriginal(bingItems);
        setBingInitial(bingItems);
      } catch (err) {
        console.error("Failed to load Bing disclaimer texts", err);
        globalErrorsStore.addErrorIfNotExists(options.commonTranslation.loadFailed);
      }

      try {
        // Load ImageGen disclaimers
        const imageGenList = await options.imageGenOptions.loadDisclaimerTexts();
        const imageGenItems = hydrateTexts("imagegen", imageGenList);
        setImageGenTexts(imageGenItems);
        setImageGenOriginal(imageGenItems);
      } catch (err) {
        console.error("Failed to load ImageGen disclaimer texts", err);
        globalErrorsStore.addErrorIfNotExists(options.commonTranslation.loadFailed);
      }

      setLoading(false);
    };

    loadBoth();

    return () => {
      globalErrorsStore.removeError(options.commonTranslation.loadFailed);
      globalErrorsStore.removeError(options.commonTranslation.saveFailed);
      setBingTexts(getAvailableDisclaimerLanguages("bing"));
      setBingOriginal(getAvailableDisclaimerLanguages("bing"));
      setBingInitial(getAvailableDisclaimerLanguages("bing"));
      setImageGenTexts(getAvailableDisclaimerLanguages("imagegen"));
      setImageGenOriginal(getAvailableDisclaimerLanguages("imagegen"));
    };
  }, [globalErrorsStore, options]);

  // Compute hasChanges for either disclaimer
  const hasChanges = !textsAreEqual(bingTexts, bingOriginal) || !textsAreEqual(imageGenTexts, imageGenOriginal);

  const onChangeBing = (language: DisclaimerLanguage, value: string) => {
    setBingTexts(prev => ({
      ...prev,
      [language]: {
        ...prev[language],
        text: value,
      },
    }));
  };

  const onChangeImageGen = (language: DisclaimerLanguage, value: string) => {
    setImageGenTexts(prev => ({
      ...prev,
      [language]: {
        ...prev[language],
        text: value,
      },
    }));
  };

  const onSave = async () => {
    if (saving) {
      return;
    }

    const bingChangedItems = (Object.keys(bingTexts) as DisclaimerLanguage[])
      .filter(lang => bingTexts[lang].text !== bingOriginal[lang].text)
      .map(lang => bingTexts[lang]);

    const imageGenChangedItems = (Object.keys(imageGenTexts) as DisclaimerLanguage[])
      .filter(lang => imageGenTexts[lang].text !== imageGenOriginal[lang].text)
      .map(lang => imageGenTexts[lang]);

    if (bingChangedItems.length === 0 && imageGenChangedItems.length === 0) {
      return;
    }

    const changedItems = [...bingChangedItems, ...imageGenChangedItems];

    setSaving(true);

    try {
      await options.saveDisclaimerTexts(changedItems);

      if (bingChangedItems.length > 0) {
        setBingOriginal(bingTexts);
      }

      if (imageGenChangedItems.length > 0) {
        setImageGenOriginal(imageGenTexts);
      }

      globalErrorsStore.removeError(options.commonTranslation.saveFailed);

      const successKeys = [
        bingChangedItems.length > 0 ? options.bingOptions.translation.saveSuccess : undefined,
        imageGenChangedItems.length > 0 ? options.imageGenOptions.translation.saveSuccess : undefined,
      ].filter((key): key is string => Boolean(key));

      successKeys.forEach(translationKey => {
        toastStore.addToast({
          severity: MessageSeverity.SUCCESS,
          summary: { translationKey },
          detail: { message: "" },
        });
      });
    } catch (err) {
      console.error("Failed to save disclaimer texts in bulk", err);
      globalErrorsStore.addErrorIfNotExists(options.commonTranslation.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    // Reset Bing based on its resetMode
    const bingResetMode = options.bingOptions.resetMode || "original";
    setBingTexts(bingResetMode === "initial" ? bingInitial : bingOriginal);

    // Reset ImageGen based on its resetMode
    setImageGenTexts(imageGenOriginal);
  };

  return {
    bingTexts,
    imageGenTexts,
    loading,
    saving,
    hasChanges,
    onChangeBing,
    onChangeImageGen,
    onSave,
    onReset,
  };
}
